import React, { useState } from 'react'
import { CommonButton } from '@/components/common'
import {
  AddIcon,
  DownloadIcon,
  EditIcon,
  SaveIcon,
  SearchIcon,
  HomeIcon,
} from '@/icons'
import {
  SubSection,
  StyledDemoGrid,
  DemoComponent,
  StyledSizeLabel,
} from '../styled.jsx'

export const ButtonSection = () => {
  const [loading, setLoading] = useState({})

  const handleLoadingToggle = buttonId => {
    setLoading(prev => ({
      ...prev,
      [buttonId]: !prev[buttonId],
    }))

    setTimeout(() => {
      setLoading(prev => ({
        ...prev,
        [buttonId]: false,
      }))
    }, 2000)
  }

  return (
    <>
      {/* Button Variants */}
      <SubSection
        title='Button Variants'
        description='Three main variants: primary (contained), secondary (outlined), and text - shown with text and icon-only buttons'
      >
        {/* Text Buttons */}
        <StyledSizeLabel variant='h6' gutterBottom>
          Text Buttons
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Primary'
            component={<CommonButton label='Primary' variant='primary' />}
          />
          <DemoComponent
            label='Secondary'
            component={<CommonButton label='Secondary' variant='secondary' />}
          />
          <DemoComponent
            label='Text'
            component={<CommonButton label='Text' variant='text' />}
          />
        </StyledDemoGrid>

        {/* Icon Buttons */}
        <StyledSizeLabel variant='h6' gutterBottom>
          Icon Buttons
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Primary Icon'
            component={
              <CommonButton iconOnly={<AddIcon />} variant='primary' />
            }
          />
          <DemoComponent
            label='Secondary Icon'
            component={
              <CommonButton iconOnly={<AddIcon />} variant='secondary' />
            }
          />
          <DemoComponent
            label='Text Icon'
            component={<CommonButton iconOnly={<AddIcon />} variant='text' />}
          />
        </StyledDemoGrid>
      </SubSection>

      {/* Button Sizes */}
      <SubSection
        title='Button Sizes'
        description='Three sizes: small, medium (default), and large - shown across all variants'
      >
        {/* Small Size */}
        <StyledSizeLabel variant='h6' gutterBottom>
          Small
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Text'
            component={<CommonButton label='Small' size='small' />}
          />
          <DemoComponent
            label='Primary Icon'
            component={
              <CommonButton
                iconOnly={<AddIcon />}
                size='small'
                variant='primary'
              />
            }
          />
          <DemoComponent
            label='Secondary Icon'
            component={
              <CommonButton
                iconOnly={<AddIcon />}
                size='small'
                variant='secondary'
              />
            }
          />
          <DemoComponent
            label='Text Icon'
            component={
              <CommonButton
                iconOnly={<AddIcon />}
                size='small'
                variant='text'
              />
            }
          />
        </StyledDemoGrid>

        {/* Medium Size */}
        <StyledSizeLabel variant='h6' gutterBottom>
          Medium
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Text'
            component={<CommonButton label='Medium' size='medium' />}
          />
          <DemoComponent
            label='Primary Icon'
            component={
              <CommonButton
                iconOnly={<AddIcon />}
                size='medium'
                variant='primary'
              />
            }
          />
          <DemoComponent
            label='Secondary Icon'
            component={
              <CommonButton
                iconOnly={<AddIcon />}
                size='medium'
                variant='secondary'
              />
            }
          />
          <DemoComponent
            label='Text Icon'
            component={
              <CommonButton
                iconOnly={<AddIcon />}
                size='medium'
                variant='text'
              />
            }
          />
        </StyledDemoGrid>

        {/* Large Size */}
        <StyledSizeLabel variant='h6' gutterBottom>
          Large
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Text'
            component={<CommonButton label='Large' size='large' />}
          />
          <DemoComponent
            label='Primary Icon'
            component={
              <CommonButton
                iconOnly={<AddIcon />}
                size='large'
                variant='primary'
              />
            }
          />
          <DemoComponent
            label='Secondary Icon'
            component={
              <CommonButton
                iconOnly={<AddIcon />}
                size='large'
                variant='secondary'
              />
            }
          />
          <DemoComponent
            label='Text Icon'
            component={
              <CommonButton
                iconOnly={<AddIcon />}
                size='large'
                variant='text'
              />
            }
          />
        </StyledDemoGrid>
      </SubSection>

      {/* Button with Icons */}
      <SubSection
        title='Buttons with Icons'
        description='Start icons, end icons, or icon-only variants'
      >
        <StyledDemoGrid>
          <DemoComponent
            label='Start Icon'
            component={
              <CommonButton
                label='Add Item'
                startIcon={<AddIcon />}
                variant='primary'
              />
            }
          />
          <DemoComponent
            label='End Icon'
            component={
              <CommonButton
                label='Download'
                endIcon={<DownloadIcon />}
                variant='secondary'
              />
            }
          />
          <DemoComponent
            label='Both Icons'
            component={
              <CommonButton
                label='Edit'
                startIcon={<EditIcon />}
                endIcon={<SaveIcon />}
                variant='text'
              />
            }
          />
        </StyledDemoGrid>
      </SubSection>

      {/* Button States */}
      <SubSection
        title='Button States'
        description='Loading and disabled states (click loading buttons to see animation)'
      >
        <StyledDemoGrid>
          <DemoComponent
            label='Loading (Click)'
            component={
              <CommonButton
                label='Click to Load'
                loading={loading.primary}
                onClick={() => handleLoadingToggle('primary')}
                variant='primary'
              />
            }
          />
          <DemoComponent
            label='Disabled'
            component={
              <CommonButton
                label='Disabled Button'
                disabled={true}
                variant='primary'
              />
            }
          />
          <DemoComponent
            label='Disabled Secondary'
            component={
              <CommonButton
                label='Disabled'
                disabled={true}
                variant='secondary'
              />
            }
          />
          <DemoComponent
            label='Loading Icon'
            component={
              <CommonButton
                iconOnly={<SearchIcon />}
                loading={loading.icon}
                onClick={() => handleLoadingToggle('icon')}
                variant='primary'
              />
            }
          />
        </StyledDemoGrid>
      </SubSection>

      {/* Special Features */}
      <SubSection
        title='Special Features'
        description='Tooltips, box shadow, and link functionality'
      >
        <StyledDemoGrid>
          <DemoComponent
            label='Tooltip (Hover)'
            component={
              <CommonButton
                label='Hover Me'
                toolTip='This is a helpful tooltip!'
                variant='primary'
              />
            }
          />
          <DemoComponent
            label='Box Shadow'
            component={
              <CommonButton
                label='Elevated'
                boxShadow={true}
                variant='secondary'
              />
            }
          />
          <DemoComponent
            label='Link Button'
            component={
              <CommonButton
                label='Visit Link'
                to='https://example.com'
                link={true}
                variant='text'
                endIcon={<HomeIcon />}
              />
            }
          />
        </StyledDemoGrid>
      </SubSection>

      {/* Color Variants */}
      <SubSection
        title='Color Variants'
        description='Different Material-UI color themes'
      >
        <StyledDemoGrid>
          <DemoComponent
            label='Primary'
            component={
              <CommonButton label='Primary' color='primary' variant='primary' />
            }
          />
          <DemoComponent
            label='Secondary'
            component={
              <CommonButton
                label='Secondary'
                color='secondary'
                variant='primary'
              />
            }
          />
          <DemoComponent
            label='Success'
            component={
              <CommonButton label='Success' color='success' variant='primary' />
            }
          />
          <DemoComponent
            label='Error'
            component={
              <CommonButton label='Error' color='error' variant='primary' />
            }
          />
          <DemoComponent
            label='Warning'
            component={
              <CommonButton label='Warning' color='warning' variant='primary' />
            }
          />
          <DemoComponent
            label='Info'
            component={
              <CommonButton label='Info' color='info' variant='primary' />
            }
          />
        </StyledDemoGrid>
      </SubSection>
    </>
  )
}
